/**
 * Import Service
 *
 * Handles parsing and validating POI import data from various formats:
 * - Single POI object
 * - Array of POIs
 * - Full POIPlannerExport
 */

import type {
  POIImportItem,
  POIImportData,
  ImportValidationResult,
  POIPlannerExport,
} from "@poi-planner/shared";

// The takeout version this build writes, alongside POIPlannerExport["version"].
const EXPORT_VERSION = 5;

// Versions this build can read. Version 4 differs from 5 only by legs: the
// library shape is identical, so rejecting a v4 file would strand every
// backup taken before legs shipped over data it fully understands.
const READABLE_EXPORT_VERSIONS = new Set([4, EXPORT_VERSION]);

/** Fill in the fields a v4 file predates, so the rest of the app sees v5. */
function upgradeTakeout(data: POIPlannerExport): POIPlannerExport {
  if (data.version === EXPORT_VERSION) return data;
  return {
    ...data,
    version: EXPORT_VERSION,
    trips: (data.trips ?? []).map((trip) => ({
      ...trip,
      legs: trip.legs ?? [],
      schedule: {
        ...trip.schedule,
        notes: (trip.schedule?.notes ?? []).map((note) => ({
          ...note,
          legIds: note.legIds ?? [],
        })),
      },
    })),
  };
}

// ── Type Guards ──

function isPOIPlannerExport(data: unknown): data is POIPlannerExport {
  return (
    typeof data === "object" &&
    data !== null &&
    "version" in data &&
    READABLE_EXPORT_VERSIONS.has((data as { version: number }).version) &&
    ("library" in data || "trips" in data)
  );
}

function isPOIArray(data: unknown): data is POIImportItem[] {
  return Array.isArray(data) && data.every(isPOIObject);
}

function isPOIObject(data: unknown): data is POIImportItem {
  return (
    typeof data === "object" &&
    data !== null &&
    "name" in data &&
    typeof (data as POIImportItem).name === "string"
  );
}

// ── Validation ──

function validatePOI(
  poi: POIImportItem,
  index: number,
): { warnings: string[]; errors: string[] } {
  const warnings: string[] = [];
  const errors: string[] = [];
  const prefix = `POI #${index + 1}`;

  // Required: name
  if (!poi.name || !poi.name.trim()) {
    errors.push(`${prefix}: Missing required field 'name'`);
  }

  // Optional: lat/lng validation
  if (poi.lat !== undefined && poi.lat !== null) {
    if (typeof poi.lat !== "number" || isNaN(poi.lat)) {
      warnings.push(`${prefix}: Invalid latitude, will be set to null`);
    } else if (poi.lat < -90 || poi.lat > 90) {
      warnings.push(`${prefix}: Latitude out of range (-90 to 90)`);
    }
  }

  if (poi.lng !== undefined && poi.lng !== null) {
    if (typeof poi.lng !== "number" || isNaN(poi.lng)) {
      warnings.push(`${prefix}: Invalid longitude, will be set to null`);
    } else if (poi.lng < -180 || poi.lng > 180) {
      warnings.push(`${prefix}: Longitude out of range (-180 to 180)`);
    }
  }

  // Optional: timeBudget validation
  if (poi.timeBudget !== undefined && poi.timeBudget !== null) {
    if (typeof poi.timeBudget !== "number" || poi.timeBudget < 0) {
      warnings.push(`${prefix}: Invalid timeBudget, will use default (60)`);
    }
  }

  // Optional: idealTimeOfDay validation
  if (poi.idealTimeOfDay !== undefined && poi.idealTimeOfDay !== null) {
    const validTimes = ["morning", "afternoon", "evening"];
    if (!validTimes.includes(poi.idealTimeOfDay)) {
      warnings.push(
        `${prefix}: Invalid idealTimeOfDay '${poi.idealTimeOfDay}', will be ignored`,
      );
    }
  }

  return { warnings, errors };
}

// ── Normalization ──

function numberOrNull(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function stringArrayOrEmpty(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function normalizePOI(input: Record<string, unknown>): POIImportItem {
  // Handle both camelCase (app) and snake_case (database) field names
  return {
    name: stringOrUndefined(input.name)?.trim() || "",
    lat: numberOrNull(input.lat),
    lng: numberOrNull(input.lng),
    timeBudget:
      typeof input.timeBudget === "number"
        ? input.timeBudget
        : typeof input.time_budget_minutes === "number"
          ? input.time_budget_minutes
          : 60,
    tags: stringArrayOrEmpty(input.tags),
    notes: stringOrNull(input.notes),
    sources: Array.isArray(input.sources) ? input.sources : [],
    googleMapsUrl:
      stringOrUndefined(input.googleMapsUrl) ??
      stringOrUndefined(input.google_maps_url),
    availability: (input.availability as POIImportItem["availability"]) ?? null,
    idealDays:
      (input.idealDays as string[] | null | undefined) ??
      (input.ideal_days as string[] | null | undefined) ??
      null,
    idealTimeOfDay:
      (input.idealTimeOfDay as POIImportItem["idealTimeOfDay"]) ??
      (input.ideal_time_of_day as POIImportItem["idealTimeOfDay"]) ??
      null,
    weatherRequirement:
      (input.weatherRequirement as POIImportItem["weatherRequirement"]) ??
      (input.weather_requirement as POIImportItem["weatherRequirement"]) ??
      null,
  };
}

// ── Main Functions ──

/**
 * Parse JSON string into POI import data
 */
export function parseImportJSON(jsonString: string): {
  data: POIImportData | null;
  error: string | null;
} {
  try {
    const parsed = JSON.parse(jsonString);
    return { data: parsed, error: null };
  } catch {
    return { data: null, error: "Invalid JSON format" };
  }
}

/**
 * Extract POIs from various import formats
 */
export function extractPOIsFromImport(data: POIImportData): POIImportItem[] {
  // Full PlotPlanGo export
  if (isPOIPlannerExport(data)) {
    return (data.library?.pois || []).map((poi) =>
      normalizePOI(poi as unknown as Record<string, unknown>),
    );
  }

  // Array of POIs
  if (isPOIArray(data)) {
    return data.map((poi) =>
      normalizePOI(poi as unknown as Record<string, unknown>),
    );
  }

  // Single POI object
  if (isPOIObject(data)) {
    return [normalizePOI(data as unknown as Record<string, unknown>)];
  }

  return [];
}

/**
 * Validate import data and return validated POIs with warnings/errors
 */
export function validateImport(data: unknown): ImportValidationResult {
  const result: ImportValidationResult = {
    valid: true,
    pois: [],
    warnings: [],
    errors: [],
  };

  // First, check if we can parse it
  if (typeof data === "string") {
    const { data: parsed, error } = parseImportJSON(data);
    if (error) {
      result.valid = false;
      result.errors.push(error);
      return result;
    }
    data = parsed;
  }

  if (
    typeof data === "object" &&
    data !== null &&
    "version" in data &&
    !READABLE_EXPORT_VERSIONS.has((data as { version?: number }).version ?? -1)
  ) {
    result.valid = false;
    result.errors.push(
      "This takeout file is from an unsupported PlotPlanGo version and can't be imported. Export again from the current app version.",
    );
    return result;
  }

  if (isPOIPlannerExport(data)) result.takeout = upgradeTakeout(data);

  // Extract POIs from the data
  const pois = extractPOIsFromImport(data as POIImportData);

  if (pois.length === 0) {
    result.valid = false;
    result.errors.push("No valid POIs found in the import data");
    return result;
  }

  // Validate each POI
  for (let i = 0; i < pois.length; i++) {
    const poi = pois[i];
    const validation = validatePOI(poi, i);

    result.warnings.push(...validation.warnings);
    result.errors.push(...validation.errors);

    // Only add POI if no errors for this specific POI
    if (validation.errors.length === 0) {
      result.pois.push(poi);
    }
  }

  // Set overall validity
  result.valid = result.pois.length > 0;

  return result;
}

/**
 * Convert POIImportItem to the format expected by createPOI
 */
export function importItemToCreateData(item: POIImportItem): {
  name: string;
  lat: number | null;
  lng: number | null;
  timeBudget: number;
  tags: string[];
  notes: string | null;
  sources: Array<{ text: string; url?: string }>;
  googleMapsUrl?: string;
  availability: { start: string; end: string } | null;
  idealDays: string[] | null;
  idealTimeOfDay: "morning" | "afternoon" | "evening" | null;
  weatherRequirement: {
    condition: string;
    sensitivity: "required" | "desired";
  } | null;
  primaryTagId: string | null;
} {
  return {
    name: item.name,
    lat: typeof item.lat === "number" && !isNaN(item.lat) ? item.lat : null,
    lng: typeof item.lng === "number" && !isNaN(item.lng) ? item.lng : null,
    timeBudget: item.timeBudget ?? 60,
    tags: item.tags ?? [],
    notes: item.notes ?? null,
    sources: item.sources ?? [],
    googleMapsUrl: item.googleMapsUrl,
    availability: item.availability ?? null,
    idealDays: item.idealDays ?? null,
    idealTimeOfDay: item.idealTimeOfDay ?? null,
    weatherRequirement: item.weatherRequirement ?? null,
    primaryTagId: null, // Will be set based on tags if needed
  };
}
